import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../lib/api.ts";
import { downloadCsv, fmtNum } from "../lib/csv.ts";
import { GroupedDemand, type View } from "../components/GroupedDemand.tsx";
import { DemandGraph } from "../components/DemandGraph.tsx";
import { DemandOverview } from "../components/DemandOverview.tsx";
import { ExportButton, ListBar } from "../components/ListBar.tsx";
import type { ReactNode } from "react";
import { seriesLabel } from "../lib/skuNames.ts";
import { coverSortRank } from "../data/coverOrder.ts";
import { baseSortRank } from "../data/baseOrder.ts";
import { BreakdownPanel } from "../components/BreakdownPanel.tsx";
import { frameSortRank } from "../data/frameOrder.ts";
import { isCarriedSunbrella } from "../data/sunbrellaCarried.ts";
import { theme } from "../theme.ts";
import type { DemandTableResponse } from "@march/shared";

export type Category =
  | "Frame"
  | "Olefin"
  | "Pacifica"
  | "Sunbrella"
  | "Base"
  | "Trim"
  | "ProtectiveCover";

export const CATEGORIES: readonly Category[] = [
  "Frame",
  "Olefin",
  "Pacifica",
  "Sunbrella",
  "Base",
  "Trim",
  "ProtectiveCover",
];

// Display names per the brand rules: Olefin shows as Solstice, Pacifica as Pacifica Marine.
export const TAB_LABEL: Record<Category, string> = {
  Frame: "Frames",
  Olefin: "Solstice covers",
  Pacifica: "Pacifica Marine covers",
  Sunbrella: "Sunbrella covers",
  Base: "Bases",
  Trim: "Trim",
  ProtectiveCover: "Protective covers",
};

/** Parts a category shows by default: any demand or a projection (and carried colours only for Sunbrella). */
export const shownRows = (rows: DemandTableResponse["rows"], category: Category) =>
  rows.filter((r) => (Object.values(r.fy_totals).some((v) => v !== 0) || r.fy_projection) && (category !== "Sunbrella" || isCarriedSunbrella(r.itemid)));

/**
 * Plan-style Demand table — every tracked SKU of a category with its
 * fiscal-year totals, laid out like the planning workbook's tabs so the
 * two can be read side by side. Rows follow the same curated
 * merchandising order the Forecast view uses.
 */
export function DemandTable({
  category,
  view,
  onShowNotes,
  badge,
}: {
  category: Category;
  view: View;
  onShowNotes?: () => void;
  badge?: ReactNode;
}) {
  const [search, setSearch] = useState("");
  // Which FY cell's "how this number is built" panel is open.
  const [breakdown, setBreakdown] = useState<{ itemid: string; fy: string } | null>(null);
  const [hideEmpty, setHideEmpty] = useState(true);
  // Sunbrella only: the CoverBase grid generates size x colour pairs March
  // does not carry. Their demand is real special-order work, so it is kept
  // but held out of the default view — mixing it in is what made the size
  // totals look incomparable to the plan.
  const [carriedOnly, setCarriedOnly] = useState(true);

  const q = useQuery<DemandTableResponse>({
    queryKey: ["demand-table", category],
    queryFn: () => apiGet<DemandTableResponse>(`/api/demand/table?category=${category}`),
  });

  // Fiscal years present, oldest first, so columns mirror the workbook.
  const fys = useMemo(() => {
    const set = new Set<string>();
    for (const r of q.data?.rows ?? []) for (const k of Object.keys(r.fy_totals)) set.add(k);
    return [...set].sort();
  }, [q.data]);

  const rows = useMemo(() => {
    let out = [...(q.data?.rows ?? [])];
    if (hideEmpty) {
      out = out.filter(
        (r) => Object.values(r.fy_totals).some((v) => v !== 0) || r.fy_projection,
      );
    }
    if (category === "Sunbrella" && carriedOnly) {
      out = out.filter((r) => isCarriedSunbrella(r.itemid));
    }
    const needle = search.trim().toLowerCase();
    if (needle) out = out.filter((r) => r.itemid.toLowerCase().includes(needle));
    // Same merchandising order as Forecast, so the sheet reads familiarly.
    return out.sort((a, b) => {
      const rank = (id: string) =>
        category === "Frame"
          ? frameSortRank(id)
          : category === "Base" || category === "Trim" || category === "ProtectiveCover"
            ? baseSortRank(id)
            : coverSortRank(id, category);
      const ar = rank(a.itemid);
      const br = rank(b.itemid);
      return ar !== br ? ar - br : a.itemid.localeCompare(b.itemid);
    });
  }, [q.data, search, hideEmpty, carriedOnly, category]);

  // What the carried-only filter is holding back, so it reads as separated
  // rather than quietly missing.
  const specialOrder = useMemo(() => {
    if (category !== "Sunbrella") return null;
    const rows = (q.data?.rows ?? []).filter((r) => !isCarriedSunbrella(r.itemid));
    const fy = q.data?.current_fy;
    const prior = fys.filter((f) => f !== fy).slice(-1)[0];
    let units = 0;
    for (const r of rows) units += prior ? (r.fy_totals[prior] ?? 0) : 0;
    const withDemand = rows.filter((r) => Object.values(r.fy_totals).some((v) => v !== 0));
    return { keys: withDemand.length, units: Math.round(units), fy: prior };
  }, [q.data, fys, category]);

  const exportCsv = () =>
    downloadCsv(
      `demand-${category.toLowerCase()}-${new Date().toISOString().slice(0, 10)}.csv`,
      rows.map((r) => ({
        SKU: r.itemid,
        ...Object.fromEntries(fys.map((fy) => [`${fy}_used`, Math.round(r.fy_totals[fy] ?? 0)])),
        ...Object.fromEntries(
          fys.map((fy) => [`${fy}_sold_alone`, Math.round(r.fy_standalone?.[fy] ?? 0)]),
        ),
        Projected: r.fy_projection?.value ?? "",
      })),
    );

  return (
    <div>
      <ListBar
        title={`${TAB_LABEL[category]} · ${q.isLoading ? "…" : rows.length} parts`}
        search={search}
        onSearch={setSearch}
      >
        {category === "Sunbrella" && (
          <label className="check">
            <input type="checkbox" checked={carriedOnly} onChange={(e) => setCarriedOnly(e.target.checked)} />
            Carried colours only
          </label>
        )}
        {badge}
        <ExportButton onClick={exportCsv} disabled={rows.length === 0} />
      </ListBar>

      {specialOrder && carriedOnly && specialOrder.keys > 0 && (
        <div style={{ fontSize: theme.font.size.xs, color: theme.color.textMuted }}>
          {specialOrder.keys} non-carried colour
          {specialOrder.keys === 1 ? "" : "s"} held back
          {specialOrder.units > 0 && specialOrder.fy
            ? ` · ${fmtNum(specialOrder.units)} units in ${specialOrder.fy}`
            : ""}{" "}
          — real special-order demand, not carried product. Untick
          &ldquo;Carried colours only&rdquo; to include it.
        </div>
      )}

      {q.isLoading && <div style={{ color: theme.color.textMuted }}>Loading…</div>}
      {q.error && (
        <div
          style={{
            padding: theme.space.md,
            background: theme.color.dangerFaint,
            color: theme.color.danger,
            borderRadius: theme.radius.md,
          }}
        >
          {(q.error as Error).message}
        </div>
      )}

      {q.data && (
        <>

          {view === "overview" ? (
            <DemandOverview rows={rows} fys={fys} currentFy={q.data.current_fy} groupOf={(id) => groupOf(id, category)}
              onBreakdown={(itemid, fy) => setBreakdown({ itemid, fy })} unit={category === "Trim" ? "yd" : "units"} />
          ) : view === "graph" ? (
            <DemandGraph rows={rows} fys={fys} currentFy={q.data.current_fy} groupOf={(id) => groupOf(id, category)} unit={category === "Trim" ? "yd" : "units"} />
          ) : (
          <GroupedDemand
            rows={rows}
            fys={fys}
            currentFy={q.data.current_fy}
            category={category}
            view="numbers"
            allOpen
            groupOf={(id) => groupOf(id, category)}
            onBreakdown={(itemid, fy) => setBreakdown({ itemid, fy })}
            emptyMessage={search.trim() ? `No SKUs match "${search.trim()}".` : "No demand data yet."}
          />
          )}
        </>
      )}
      {q.data && (
        <p className="fine">
          {rows.length} parts · fiscal years run July–June · {q.data.current_fy} is still in progress.{" "}
          {hideEmpty ? (
            <button type="button" className="btn link" onClick={() => setHideEmpty(false)}>Show parts with no demand</button>
          ) : (
            <button type="button" className="btn link" onClick={() => setHideEmpty(true)}>Hide parts with no demand</button>
          )}
          {onShowNotes && (
            <>
              {" · "}
              <button type="button" className="btn link" onClick={onShowNotes}>Why {TAB_LABEL[category].toLowerCase()} differ from the plan</button>
            </>
          )}
        </p>
      )}
      {breakdown && (
        <BreakdownPanel
          itemid={breakdown.itemid}
          fy={breakdown.fy}
          onClose={() => setBreakdown(null)}
        />
      )}
    </div>
  );
}

/** Product-line groups: frames by series, covers by size, bases and trim by code prefix. */
function groupOf(itemid: string, category: Category): { key: string; label: string } {
  if (category === "Frame") {
    const code = itemid.match(/^[A-Z]+/)?.[0] ?? itemid;
    return { key: code, label: seriesLabel(code) };
  }
  if (category === "Olefin" || category === "Pacifica" || category === "Sunbrella") {
    const size = itemid.match(/^C(\d{3,4})/)?.[1];
    const SIZES: Record<string, string> = { "118": "11 ft round", "604": "6 ft square", "758": "7.5 ft round", "908": "9 ft round" };
    return size ? { key: size, label: `${SIZES[size] ?? `C${size}`} covers` } : { key: "other", label: "Other" };
  }
  if (category === "Base") {
    const code = itemid.match(/^[A-Z]+/)?.[0] ?? "Other";
    return { key: code, label: `${code} bases` };
  }
  return { key: "all", label: "All" };
}
